import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCIES, getActiveCurrencies } from '@/lib/currency';
import type { CurrencyDefinition } from '@/types/domain';

const STORAGE_KEY = 'vg_currency';

interface CurrencySwitcherProps {
  dark?: boolean;
}

export default function CurrencySwitcher({ dark = false }: CurrencySwitcherProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<string>('TRY');
  const ref = useRef<HTMLDivElement>(null);

  const active = getActiveCurrencies();
  const current: CurrencyDefinition = active.find((c) => c.code === code) ?? active[0];

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && active.some((c) => c.code === stored)) {
      setCode(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const change = (next: string) => {
    setCode(next);
    window.localStorage.setItem(STORAGE_KEY, next);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('currency.label')}
        aria-expanded={open}
        className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
          dark
            ? 'border-foreground-300/40 text-foreground-50 hover:border-primary-400 hover:text-primary-400'
            : 'border-foreground-300/40 text-foreground-950 hover:border-primary-500 hover:text-primary-600'
        }`}
      >
        <span className="text-xs font-semibold tracking-wide">{current.code}</span>
        <i
          className={`ri-arrow-down-s-line text-sm transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        ></i>
      </button>

      {open && (
        <div className="absolute end-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-lg border border-foreground-200/60 bg-background-50 py-1">
          {CURRENCIES.map((currency) => {
            const isActive = currency.active;
            const isCurrent = currency.code === current.code;
            return (
              <button
                key={currency.code}
                type="button"
                disabled={!isActive}
                onClick={() => isActive && change(currency.code)}
                className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors ${
                  isActive ? 'cursor-pointer hover:bg-primary-100/60' : 'cursor-not-allowed opacity-45'
                } ${isCurrent ? 'font-semibold text-primary-600' : 'text-foreground-700'}`}
              >
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <span>{currency.code}</span>
                  <span className="text-foreground-400">{currency.symbol}</span>
                </span>
                {isCurrent ? (
                  <i className="ri-check-line text-primary-500" aria-hidden="true"></i>
                ) : !isActive ? (
                  <span className="text-[10px] uppercase tracking-wide text-foreground-400">
                    {t('currency.planned')}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}